import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import { Plus, Trash2 } from "lucide-react";
import type { GraphDbProperty } from "@/lib/graph-db-schema";

interface GraphDbPropertiesEditorProps {
  value: GraphDbProperty[];
  onChange: (rows: GraphDbProperty[]) => void;
  /** Problem with the list, shown under it. */
  error?: string | null;
  disabled?: boolean;
}

/**
 * Editor for the property list on a node type or an edge type: name, optional
 * type text, and a required flag per row. It edits the whole list — the
 * backend replaces it on save — so what is shown is exactly what gets sent.
 */
export default function GraphDbPropertiesEditor({ value, onChange, error, disabled }: GraphDbPropertiesEditorProps) {
  const set = (index: number, patch: Partial<GraphDbProperty>) =>
    onChange(value.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <label className="text-xs font-medium text-white">Properties</label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => onChange([...value, { name: "" }])}
        >
          <Plus className="h-3.5 w-3.5 mr-1" /> Add property
        </Button>
      </div>

      {value.length === 0 ? (
        <p className="text-xs text-muted">No properties yet. They are a note of what the type carries, not data.</p>
      ) : (
        <div className="space-y-2">
          {value.map((row, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input
                value={row.name}
                onChange={(e) => set(i, { name: e.target.value })}
                placeholder="name"
                aria-label={`Property ${i + 1} name`}
                disabled={disabled}
                className="bg-surface text-white flex-1"
              />
              <Input
                value={row.type ?? ""}
                onChange={(e) => set(i, { type: e.target.value })}
                placeholder="type (optional)"
                aria-label={`Property ${i + 1} type`}
                disabled={disabled}
                className="bg-surface text-white w-40"
              />
              <label className="flex items-center gap-1.5 text-xs text-white whitespace-nowrap">
                <Checkbox
                  checked={!!row.required}
                  onCheckedChange={(checked) => set(i, { required: checked === true })}
                  disabled={disabled}
                  aria-label={`Property ${i + 1} required`}
                />
                required
              </label>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={disabled}
                aria-label={`Remove property ${i + 1}`}
                onClick={() => onChange(value.filter((_, j) => j !== i))}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
      {error && <p className="text-xs text-error">{error}</p>}
    </div>
  );
}
